import { cn } from "../lib/utils";

/**
 * Six MS monogram concepts that blend skincare (drops, leaves, layers, curves)
 * with medication (capsules, pills, shield, cross). Each is a self-contained
 * SVG inside a 40x40 viewBox so they all render identically in the preview
 * and downscale cleanly to favicons.
 *
 * Pass `color` + `accent` (a small skincare-green pulse) to theme each one.
 */

const baseSvgProps = {
  width: 40,
  height: 40,
  viewBox: "0 0 40 40",
  fill: "none",
  role: "img",
};

/* ------------------------------------------------------------------ */
/* Concept 1 — "Capsule M·S"                                           */
/* Two upright pill capsules form the legs of an M; an S-curve threads */
/* through them, tying medication + skincare in one mark.              */
/* ------------------------------------------------------------------ */
export function ConceptCapsuleMS({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Capsule M S">
      {/* Left pill */}
      <rect x="5.5" y="8" width="6.5" height="24" rx="3.25" fill={color} />
      {/* Pill seam (split line — classic capsule cue) */}
      <line x1="5.5" y1="20" x2="12" y2="20" stroke="#fff" strokeWidth="0.9" />
      {/* Right pill */}
      <rect x="28" y="8" width="6.5" height="24" rx="3.25" fill={color} />
      <line x1="28" y1="20" x2="34.5" y2="20" stroke="#fff" strokeWidth="0.9" />
      {/* S-curve threading the two pills */}
      <path
        d="M 12 14 C 18 14, 16 22, 22 22 C 28 22, 26 30, 28 26"
        stroke={color}
        strokeWidth="2.6"
        strokeLinecap="round"
        fill="none"
      />
      {/* Skincare-green safety pulse */}
      <circle cx="34" cy="8" r="2" fill={accent} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Concept 2 — "Droplet-M"                                             */
/* Geometric M where the central valley dips into a teardrop, evoking  */
/* a serum drop nesting inside the wordmark.                           */
/* ------------------------------------------------------------------ */
export function ConceptDropletM({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Droplet M">
      {/* M outline as a single bold stroke */}
      <path
        d="M 5 33 L 5 10 L 14 10 L 20 22 L 26 10 L 35 10 L 35 33"
        stroke={color}
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="none"
      />
      {/* Serum droplet sitting in the valley */}
      <path
        d="M 20 16 C 16 21, 16 26, 20 27 C 24 26, 24 21, 20 16 Z"
        fill={accent}
      />
      {/* Highlight on droplet */}
      <ellipse cx="18.5" cy="20" rx="0.9" ry="1.4" fill="#fff" opacity="0.85" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Concept 3 — "Continuous MS Ligature"                                */
/* A calligraphic single stroke that paints an M peak then flows       */
/* directly into a soft S — pure brushwork, very serum-y.              */
/* ------------------------------------------------------------------ */
export function ConceptLigatureMS({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="MS ligature">
      {/* Up, peak, down, peak, then S into the lower-right */}
      <path
        d="
          M 5 32
          L 8 10
          L 15 22
          L 22 10
          C 24 16, 32 14, 32 19
          C 32 24, 22 22, 24 28
          C 26 32, 32 31, 35 28
        "
        stroke={color}
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="35" cy="9" r="2" fill={accent} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Concept 4 — "Hex Cell MS"                                           */
/* A hexagonal molecular cell (lab / clinical) containing tight MS     */
/* letters with a leaf curl on the S terminal.                         */
/* ------------------------------------------------------------------ */
export function ConceptHexMS({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Hex MS">
      {/* Hex container */}
      <path
        d="M 20 3 L 35 11 L 35 29 L 20 37 L 5 29 L 5 11 Z"
        stroke={color}
        strokeWidth="1.8"
        fill="none"
        strokeLinejoin="round"
      />
      {/* M */}
      <path
        d="M 10 27 L 10 14 L 14 21 L 18 14 L 18 27"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* S with leafy bottom curl */}
      <path
        d="M 30 15 C 24 14, 22 19, 26 21 C 30 23, 30 27, 24 27"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Leaf accent at hex tip */}
      <path
        d="M 20 3 C 22 6, 22 8, 20 9 C 18 8, 18 6, 20 3 Z"
        fill={accent}
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Concept 5 — "Layered Skin M"                                        */
/* M built from three soft horizontal layers (epidermis / dermis /     */
/* hypodermis), with an S formed by the negative space between them.   */
/* ------------------------------------------------------------------ */
export function ConceptLayeredM({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Layered M">
      {/* Layer 1 (top, thinnest) */}
      <path
        d="M 5 12 C 12 8, 16 16, 20 12 C 24 8, 28 16, 35 12"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Layer 2 (middle, the "S" wave reads here) */}
      <path
        d="M 5 20 C 12 16, 16 24, 20 20 C 24 16, 28 24, 35 20"
        stroke={color}
        strokeWidth="2.6"
        strokeLinecap="round"
        fill="none"
        opacity="0.85"
      />
      {/* Layer 3 (bottom, thickest) */}
      <path
        d="M 5 28 C 12 24, 16 32, 20 28 C 24 24, 28 32, 35 28"
        stroke={color}
        strokeWidth="3.2"
        strokeLinecap="round"
        fill="none"
        opacity="0.55"
      />
      {/* Tiny verified pulse */}
      <circle cx="35" cy="8" r="1.8" fill={accent} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Concept 6 — "Shield MS"                                             */
/* Rounded apothecary shield (medication-safety cue) with a sculpted   */
/* MS ligature inside; soft drop sits where the cross would be.        */
/* ------------------------------------------------------------------ */
export function ConceptShieldMS({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Shield MS">
      {/* Shield silhouette */}
      <path
        d="M 20 4
           C 28 4, 33 6, 34 8
           L 34 22
           C 34 30, 27 35, 20 37
           C 13 35, 6 30, 6 22
           L 6 8
           C 7 6, 12 4, 20 4 Z"
        fill={color}
      />
      {/* M (white inside shield) */}
      <path
        d="M 11 26 L 11 14 L 15 21 L 19 14 L 19 26"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* S (white inside shield) */}
      <path
        d="M 29 15 C 24 14, 23 19, 26 20 C 30 21, 30 25, 24 25"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Skincare droplet at shield crown */}
      <path
        d="M 20 6 C 17 9, 17 12, 20 13 C 23 12, 23 9, 20 6 Z"
        fill={accent}
      />
    </svg>
  );
}

/* ================================================================== */
/* ROUND 3 — Reference-style: M-droplet + medical cross (monoline)     */
/* User reference: thin single-stroke black line, no colors, no fills, */
/* with a small medical cross inside. Variants tweak peak shape, S     */
/* curl presence, and cross placement.                                 */
/* ================================================================== */

/* V1 — Reference faithful: M-droplet outline + inner S curl + cross   */
export function ConceptV3_RefDroplet({ color = "#111", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)}
         aria-label="Reference droplet" style={{ color }}>
      <path
        d="M 10.5 8.5 L 14.5 3.2 L 20 11 L 25.5 3.2 L 29.5 8.5
           C 33.5 12, 34.5 20, 32.5 27.5
           C 30 34, 24.5 37.5, 20 37.5
           C 15.5 37.5, 10 34, 7.5 27.5
           C 5.5 20, 6.5 12, 10.5 8.5 Z"
        stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" fill="none" />
      <path
        d="M 25.5 3.2 C 24 9, 21.5 13, 22.5 19 C 23.5 24.5, 20 27, 21.5 31.5"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
      <path
        d="M 11.5 27.5 L 17.5 27.5 M 14.5 24.5 L 14.5 30.5"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/* V2 — Cleaner: drop the inner S curl, keep only outline + cross      */
export function ConceptV3_DropletPlusCross({ color = "#111", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)}
         aria-label="Droplet + cross" style={{ color }}>
      <path
        d="M 10.5 8.5 L 14.5 3.2 L 20 11 L 25.5 3.2 L 29.5 8.5
           C 33.5 12, 34.5 20, 32.5 27.5
           C 30 34, 24.5 37.5, 20 37.5
           C 15.5 37.5, 10 34, 7.5 27.5
           C 5.5 20, 6.5 12, 10.5 8.5 Z"
        stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" fill="none" />
      <path
        d="M 16 28 L 24 28 M 20 24 L 20 32"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/* V3 — Asymmetric peaks (right slightly taller) + S curl + cross      */
export function ConceptV3_AsymPeaks({ color = "#111", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)}
         aria-label="Asymmetric peaks" style={{ color }}>
      <path
        d="M 10.5 9 L 14.5 4 L 20 11 L 26 2.5 L 30 8.5
           C 33.8 12, 34.8 20, 32.5 27.5
           C 30 34, 24.5 37.5, 20 37.5
           C 15.5 37.5, 10 34, 7.5 27.5
           C 5.5 20, 6.5 12.5, 10.5 9 Z"
        stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" fill="none" />
      <path
        d="M 26 2.5 C 24 9, 21.5 13, 22.5 19 C 23.5 24.5, 20 27, 21.5 31.5"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
      <path
        d="M 11.5 27.5 L 17.5 27.5 M 14.5 24.5 L 14.5 30.5"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/* V4 — Softer rounded peaks (no sharp tips) + S curl + cross          */
export function ConceptV3_RoundedPeaks({ color = "#111", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)}
         aria-label="Rounded peaks" style={{ color }}>
      <path
        d="M 10.5 8.5
           C 12 5, 14.5 3.5, 15 4.5
           L 20 11
           C 23 6, 25 3.5, 26 4.5
           C 27 5, 29 7, 29.5 8.5
           C 33.5 12, 34.5 20, 32.5 27.5
           C 30 34, 24.5 37.5, 20 37.5
           C 15.5 37.5, 10 34, 7.5 27.5
           C 5.5 20, 6.5 12, 10.5 8.5 Z"
        stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" fill="none" />
      <path
        d="M 25.5 4.5 C 24 9, 21.5 13, 22.5 19 C 23.5 24.5, 20 27, 21.5 31.5"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
      <path
        d="M 11.5 27.5 L 17.5 27.5 M 14.5 24.5 L 14.5 30.5"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
    </svg>
  );
}

export const LOGO_CONCEPTS_V3 = [
  { id: "v3-ref",       name: "Reference (M-droplet + S curl + cross)", Component: ConceptV3_RefDroplet,
    blurb: "Faithful to the reference: sharp M peaks, inner S-curl, small medical cross." },
  { id: "v3-clean",     name: "Cleaner: droplet + cross only",          Component: ConceptV3_DropletPlusCross,
    blurb: "Same outline, no inner curl. Cross moved to dead-center for balance." },
  { id: "v3-asym",      name: "Asymmetric peaks",                       Component: ConceptV3_AsymPeaks,
    blurb: "Right peak slightly taller — gives the mark a gentle forward lean." },
  { id: "v3-rounded",   name: "Softer rounded peaks",                   Component: ConceptV3_RoundedPeaks,
    blurb: "Same shape but with softer, more organic peak tops." },
];

/* ================================================================== */
/* ROUND 2 — Ligature × Capsule hybrids                                */
/* User feedback: liked the ligature flow + capsule feel, but the      */
/* original capsule read as "M only". These variants keep the          */
/* calligraphic MS flow AND make the S visually equal/dominant.        */
/* ================================================================== */

/* V1 — "MS in Capsule" — single horizontal pill with italic m·s split  */
export function ConceptV2_PillMSInside({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="MS in capsule">
      <rect x="3" y="13" width="34" height="14" rx="7" fill={color} />
      {/* Capsule seam dividing m / s halves */}
      <line x1="20" y1="13.5" x2="20" y2="26.5" stroke="#fff" strokeWidth="0.7" opacity="0.45" />
      <text x="12.5" y="24.2" textAnchor="middle"
        fill="#fff" fontFamily="Georgia, 'Times New Roman', serif"
        fontStyle="italic" fontWeight="600" fontSize="13">m</text>
      <text x="27.5" y="24.2" textAnchor="middle"
        fill="#fff" fontFamily="Georgia, 'Times New Roman', serif"
        fontStyle="italic" fontWeight="600" fontSize="13">s</text>
      <circle cx="35.5" cy="11" r="1.9" fill={accent} />
    </svg>
  );
}

/* V2 — "Calligraphic M + Capsule-S" — M is brushwork, S is a thick   */
/* uniform pill-tube stroke with a tiny seam mark.                     */
export function ConceptV2_CapsuleS({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Capsule S">
      {/* M — light calligraphic */}
      <path
        d="M 4 32 L 7 9 L 13 22 L 19 9 L 19 32"
        stroke={color}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* S — fat pill-tube stroke (uniform width, capsule cue) */}
      <path
        d="M 35 14
           C 31 11, 24 12, 24 17
           C 24 21, 35 21, 35 26
           C 35 31, 28 32, 23 29"
        stroke={color}
        strokeWidth="3.6"
        strokeLinecap="round"
        fill="none"
      />
      {/* Capsule seam cutting across the S body */}
      <line x1="28" y1="20.5" x2="31" y2="20.5" stroke="#fff" strokeWidth="0.9" opacity="0.85" />
      <circle cx="36" cy="9" r="1.8" fill={accent} />
    </svg>
  );
}

/* V3 — "Twin Pills + S Drape" — two slim upright capsules as M legs    */
/* with a generous calligraphic S draped between (S dominates).         */
export function ConceptV2_TwinPillSDrape({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Twin pill S drape">
      {/* Slim pills */}
      <rect x="6"  y="12" width="4.4" height="22" rx="2.2" fill={color} />
      <rect x="29.6" y="12" width="4.4" height="22" rx="2.2" fill={color} />
      {/* Pill seams */}
      <line x1="6"    y1="23" x2="10.4" y2="23" stroke="#fff" strokeWidth="0.7" opacity="0.55" />
      <line x1="29.6" y1="23" x2="34"   y2="23" stroke="#fff" strokeWidth="0.7" opacity="0.55" />
      {/* Big calligraphic S draping between the pills */}
      <path
        d="M 10.5 14
           C 18 14, 16 21, 22 21
           C 28 21, 26 28, 30 30"
        stroke={color}
        strokeWidth="3.2"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="34" cy="9" r="1.8" fill={accent} />
    </svg>
  );
}

/* V4 — "Pill-Tube MS" — ONE thick uniform-width stroke that traces an  */
/* M then an S, with two thin perpendicular seam marks (capsule cue).   */
export function ConceptV2_PillTubeMS({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Pill tube MS">
      <path
        d="M 4 32
           L 7 9
           L 14 22
           L 21 9
           C 23 13, 32 11, 32 17
           C 32 22, 22 21, 24 26
           C 26 31, 32 31, 34 28"
        stroke={color}
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Two perpendicular seam marks — one in M leg, one in S body */}
      <line x1="6.4" y1="20" x2="9" y2="20.4" stroke={accent} strokeWidth="1.2" />
      <line x1="27" y1="20.5" x2="30" y2="20" stroke={accent} strokeWidth="1.2" />
    </svg>
  );
}

/* V5 — "Capsule-bowl MS" — calligraphic MS where the bottom bowl of    */
/* the S is a horizontal pill capsule lying flat (filled).              */
export function ConceptV2_CapsuleBowlMS({ color = "#111", accent = "#22c55e", className }) {
  return (
    <svg {...baseSvgProps} className={cn("shrink-0", className)} aria-label="Capsule bowl MS">
      {/* M + upper S as continuous calligraphy */}
      <path
        d="M 4 30
           L 7 9
           L 13 21
           L 19 9
           C 21 12, 28 11, 28 16
           C 28 20, 21 20, 21 24"
        stroke={color}
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Pill capsule = the bottom bowl of the S */}
      <rect x="14" y="24" width="22" height="8" rx="4" fill={color} />
      {/* Capsule seam */}
      <line x1="25" y1="24.5" x2="25" y2="31.5" stroke="#fff" strokeWidth="0.7" opacity="0.45" />
      <circle cx="35" cy="9" r="1.8" fill={accent} />
    </svg>
  );
}

export const LOGO_CONCEPTS_V2 = [
  { id: "v2-pillmsinside", name: "MS in Capsule",         Component: ConceptV2_PillMSInside,
    blurb: "Horizontal pill capsule split into an italic m | s — the seam reads as a real pill seam." },
  { id: "v2-capsules",     name: "Calligraphic M · Capsule-S", Component: ConceptV2_CapsuleS,
    blurb: "Light brush M paired with a fat pill-tube S that has a tiny capsule seam." },
  { id: "v2-twinpill",     name: "Twin Pills + S Drape",  Component: ConceptV2_TwinPillSDrape,
    blurb: "Two slim pill capsules flank a bold draping calligraphic S — the S clearly leads." },
  { id: "v2-tubems",       name: "Pill-Tube MS",          Component: ConceptV2_PillTubeMS,
    blurb: "One continuous pill-tube stroke draws M → S, with two seam marks for capsule cue." },
  { id: "v2-bowl",         name: "Capsule-Bowl MS",       Component: ConceptV2_CapsuleBowlMS,
    blurb: "Calligraphic MS where the S's bowl is replaced by a flat horizontal pill capsule." },
];

export const LOGO_CONCEPTS = [
  { id: "capsule",   name: "Capsule M·S",       Component: ConceptCapsuleMS,
    blurb: "Two pills form the M; a serpentine S threads through. Pure med + skincare." },
  { id: "droplet",   name: "Droplet M",         Component: ConceptDropletM,
    blurb: "Geometric M cradling a serum droplet in its valley." },
  { id: "ligature",  name: "MS Ligature",       Component: ConceptLigatureMS,
    blurb: "One continuous calligraphic stroke that draws an M then flows into an S." },
  { id: "hex",       name: "Hex Cell MS",       Component: ConceptHexMS,
    blurb: "Molecular cell containing crisp MS letters; leafy crown for skincare." },
  { id: "layered",   name: "Layered Skin M",    Component: ConceptLayeredM,
    blurb: "Three soft skin-layer waves; reads as both M and a hidden S." },
  { id: "shield",    name: "Shield MS",         Component: ConceptShieldMS,
    blurb: "Apothecary shield housing the MS monogram, crowned with a serum drop." },
];
